import { CommonModule } from '@angular/common';
import {
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    computed,
    inject,
    signal
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { TilbyCurrencyPipe } from '@tilby/tilby-ui-lib/pipes/tilby-currency';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import {
	ScreenOrientationService
} from 'src/app/core';

import {
    BaseInputButton
} from '@tilby/tilby-ui-lib/models';

import {
    $state,
} from 'app/ajs-upgraded-providers';

import {
    Sales
} from 'tilby-models';

import {
    ConnectionService
} from 'src/app/core';

import {
    AlertDialogService,
    PrinterErrorFiscalDialogService
} from 'src/app/dialogs';

import {
    ActiveSalePaymentService
} from 'src/app/features/cashregister/services/active-sale-payment.service';

import {
    ActiveSaleService,
    CashregisterStateService,
    SalePaymentService
} from 'src/app/features/cashregister';


@Component({
	selector: 'app-payments-action-buttons',
	templateUrl: './payments-action-buttons.component.html',
	styleUrls: ['./payments-action-buttons.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		MatButtonModule,
		TilbyCurrencyPipe,
		MatButtonToggleModule,
		TranslateModule,
		MatIconModule,
		MatProgressSpinnerModule
	]
})
export class PaymentsActionButtonsComponent implements OnInit, OnChanges {
	private readonly screenOrientationService = inject(ScreenOrientationService);
    private readonly activeSalePaymentService = inject(ActiveSalePaymentService);
    private readonly activeSaleService = inject(ActiveSaleService);
    private readonly alertDialogService = inject(AlertDialogService);
    private readonly cashregisterStateService = inject(CashregisterStateService);
    private readonly connectionService = inject(ConnectionService);
    private readonly printerErrorFiscalService = inject(PrinterErrorFiscalDialogService);
    private readonly salePaymentService = inject(SalePaymentService);
    private readonly stateService = inject($state);

    protected isMobilePortrait = this.screenOrientationService.isMobilePortrait;

    ngOnInit(): void {
        this.saleChanged(this.sale);
    }

    protected backActionButton?: BaseInputButton | undefined;
    protected payAndPrintActionButton: BaseInputButton | undefined;

    protected saleToPay?: number;
    protected salePaid?: number;
    protected saleChange?: number | null;

    protected isLoading = this.activeSaleService.paymentInProgress;

    @Input({ required: true }) sale?: Sales;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.sale) {
            this.saleChanged(changes.sale.currentValue);
        }
    }

    private async saleChanged(saleValue?: Sales) {
        if (!saleValue) {
            return;
        }

        this.saleToPay = this.salePaymentService.getAmountLeftToPay(saleValue);
        this.salePaid = this.salePaymentService.getPaid(saleValue);
        this.saleChange = this.salePaymentService.getSaleChange(saleValue);

        this.backActionButton = {
            name: 'CASHREGISTER.PAYMENTS.BACK',
            click: () => this.back(),
            icon: signal('arrow_back'),
        };

        const hasPayments = saleValue.payments?.length! > 0;
        const canPay = hasPayments && this.saleToPay <= 0;

        this.payAndPrintActionButton = {
            name: 'CASHREGISTER.PAYMENTS.PAY_AND_PRINT',
            click: () => this.payAndPrint(),
            class: this.saleToPay! > 0 ? 'tw-bg-gray-300' : 'tilby-body-background-primary-color',
            icon: signal('check'),
            isDisable: !canPay ? signal(true) : computed(() => this.activeSaleService.paymentInProgress() || this.activeSaleService.lockPaymentButtons())
        };
    }

    async payAndPrint() {
        if (this.activeSaleService.paymentInProgress() || this.activeSaleService.lockPaymentButtons()) {
            return;
        }

        if (this.activeSaleService.isPaymentsOnSaleEmpty()) {
            return this.alertDialogService.openDialog({ data: { messageLabel: 'CASHREGISTER.ACTIVE_SALE.IMPOSSIBLE_TO_PRINT_WITHOUT_PAYMENT_TYPE' } });
        }

        if (!this.connectionService.isOnline() && this.activeSaleService.hasOnlineDigitalPayment()) {
            return this.alertDialogService.openDialog({ data: { messageLabel: 'CASHREGISTER.ACTIVE_SALE.DIGITAL_PAYMENT_NOT_AVAILABLE_OFFLINE' } });
        }

        try {
            await this.activeSalePaymentService.emitSale({ skipCheckPayments: true });

            this.stateService.go('app.new.cashregister.content.showcase', {}, { inherit: false });
        } catch (error) {
            this.printerErrorFiscalService.openDialog({ data: { error, options: { printerId: this.activeSaleService.printerDocumentData?.printer.id } } });
        }
    }

    private back() {
        this.cashregisterStateService.back();
    }
}
